import Dexie from 'dexie';
import type { Table } from 'dexie';
import { lifecycleScanRejection } from '@bre-ticketing/domain';
import type { TicketStatus } from '@bre-ticketing/contracts';
import { fromBase64url, importPerformanceKey, importSeasonPassKey, parseCredential, verifyCredential } from '@bre-ticketing/ticket-credentials';
import type { Bundle, BundleDelta, BundlePass, BundleTicket, InputMethod, OfflineScanUpload, ScanOutcome, ScanResult } from './api';
import { decidePassOffline } from './passes';
import { dateWords } from './terms';

/**
 * Offline operation (docs/scanner.md §5): the bundle lives in IndexedDB
 * (Dexie) with this performance's verification key imported as a
 * non-extractable CryptoKey, so script can verify with it but never read it
 * back. The offline decision mirrors the server's order — signature,
 * lifecycle, window, zone, local duplicate — and every acceptance is
 * provisional (OFFLINE_ACCEPTED) until the sync adjudicates it.
 */

export interface StoredBundle {
  performance_id: string;
  bundle_id: string;
  bundle_version: number;
  generated_at: string;
  /** The server's clock at the last full or delta sync; the next delta asks for changes since then. */
  synced_at: string;
  expires_at: string;
  performance: Bundle['performance'];
  gate_zones: Record<string, string[]>;
  ticket_count: number;
  /** Whether admitting from this bundle with no network is allowed (D-329); absent means yes. */
  offline_admission?: boolean;
}

export interface StoredTicket extends BundleTicket {
  performance_id: string;
}

export interface LocalAdmission {
  tid: string;
  performance_id: string;
  scan_id: string;
  at: string;
  gate_id: string | null;
  gate_name: string | null;
}

export interface QueuedScan extends OfflineScanUpload {
  queued_at: string;
  seat_label: string | null;
}

interface StoredKey {
  performance_id: string;
  generation: number;
  key: CryptoKey;
}

/** A season pass, per performance so a wipe takes it with the bundle (D-373). */
export interface StoredPass extends BundlePass {
  key: string;
  performance_id: string;
}

/** Tonight's season key, non-extractable like K_p, with the season and the game's date. */
interface StoredPassKey {
  performance_id: string;
  season_id: string;
  game_date: string;
  generation: number;
  key: CryptoKey;
}

/** A pass let in on this device while offline, before the server made its ticket. */
interface LocalPassAdmission {
  key: string;
  performance_id: string;
  pass_id: string;
  scan_id: string;
  at: string;
}

const passKey = (performanceId: string, passId: string) => `${performanceId}:${passId}`;

class ScannerDb extends Dexie {
  bundles!: Table<StoredBundle, string>;
  tickets!: Table<StoredTicket, string>;
  admissions!: Table<LocalAdmission, string>;
  scan_queue!: Table<QueuedScan, string>;
  keys!: Table<StoredKey, string>;
  passes!: Table<StoredPass, string>;
  pass_keys!: Table<StoredPassKey, string>;
  pass_admissions!: Table<LocalPassAdmission, string>;
  constructor() {
    super('bre-scanner');
    this.version(1).stores({
      bundles: 'performance_id',
      tickets: 'tid, performance_id, short_code',
      admissions: 'tid, performance_id',
      scan_queue: 'scan_id, performance_id',
      keys: 'performance_id',
    });
    // Season passes (D-373).
    this.version(2).stores({
      bundles: 'performance_id',
      tickets: 'tid, performance_id, short_code, pass',
      admissions: 'tid, performance_id',
      scan_queue: 'scan_id, performance_id',
      keys: 'performance_id',
      passes: 'key, performance_id',
      pass_keys: 'performance_id',
      pass_admissions: 'key, performance_id',
    });
  }
}

export const db = new ScannerDb();

export async function storeBundle(bundle: Bundle): Promise<StoredBundle> {
  const key = await importPerformanceKey(fromBase64url(bundle.verification_key.raw));
  const passes = bundle.season_passes ?? null;
  const seasonKey = passes ? await importSeasonPassKey(fromBase64url(passes.key.raw)) : null;
  const stored: StoredBundle = {
    performance_id: bundle.performance.id,
    bundle_id: bundle.bundle_id,
    bundle_version: bundle.bundle_version,
    generated_at: bundle.generated_at,
    synced_at: bundle.generated_at,
    expires_at: bundle.expires_at,
    performance: bundle.performance,
    gate_zones: bundle.gate_zones,
    ticket_count: bundle.tickets.length,
    offline_admission: bundle.offline_admission !== false,
  };
  await db.transaction('rw', [db.bundles, db.tickets, db.keys, db.passes, db.pass_keys], async () => {
    await db.tickets.where('performance_id').equals(bundle.performance.id).delete();
    await db.tickets.bulkPut(bundle.tickets.map((t) => ({ ...t, performance_id: bundle.performance.id })));
    await db.keys.put({ performance_id: bundle.performance.id, generation: bundle.verification_key.generation, key });
    await db.passes.where('performance_id').equals(bundle.performance.id).delete();
    await db.pass_keys.delete(bundle.performance.id);
    if (passes && seasonKey) {
      await db.passes.bulkPut(passes.passes.map((p) => ({ ...p, key: passKey(bundle.performance.id, p.id), performance_id: bundle.performance.id })));
      await db.pass_keys.put({ performance_id: bundle.performance.id, season_id: passes.season_id, game_date: passes.game_date, generation: passes.key.generation, key: seasonKey });
    }
    await db.bundles.put(stored);
  });
  return stored;
}

/** Changed tickets and admissions from other gates; a revoked or expired bundle wipes everything for the performance. */
export async function applyDelta(performanceId: string, delta: BundleDelta): Promise<{ wiped: boolean; changed: number }> {
  if (delta.revoked) {
    await wipePerformance(performanceId);
    return { wiped: true, changed: 0 };
  }
  await db.transaction('rw', db.bundles, db.tickets, db.passes, async () => {
    if (delta.tickets.length > 0) await db.tickets.bulkPut(delta.tickets.map((t) => ({ ...t, performance_id: performanceId })));
    // A pass suspended, revoked or reissued since the last sync (D-373).
    if (delta.season_passes?.length) await db.passes.bulkPut(delta.season_passes.map((p) => ({ ...p, key: passKey(performanceId, p.id), performance_id: performanceId })));
    await db.bundles.update(performanceId, { synced_at: delta.generated_at, expires_at: delta.expires_at, ...(delta.offline_admission === undefined ? {} : { offline_admission: delta.offline_admission }) });
  });
  return { wiped: false, changed: delta.tickets.length + (delta.season_passes?.length ?? 0) };
}

export async function wipePerformance(performanceId: string): Promise<void> {
  await db.transaction('rw', [db.bundles, db.tickets, db.keys, db.admissions, db.passes, db.pass_keys, db.pass_admissions], async () => {
    await db.tickets.where('performance_id').equals(performanceId).delete();
    await db.admissions.where('performance_id').equals(performanceId).delete();
    await db.keys.delete(performanceId);
    await db.passes.where('performance_id').equals(performanceId).delete();
    await db.pass_keys.delete(performanceId);
    await db.pass_admissions.where('performance_id').equals(performanceId).delete();
    await db.bundles.delete(performanceId);
  });
}

/** Bundles past their expiry are deleted on sight, keys included (§5.2). */
export async function expireBundles(now: string): Promise<string[]> {
  const expired = (await db.bundles.toArray()).filter((b) => b.expires_at <= now);
  for (const bundle of expired) await wipePerformance(bundle.performance_id);
  return expired.map((b) => b.performance_id);
}

export async function storedBundles(): Promise<StoredBundle[]> {
  return db.bundles.toArray();
}

// --- The offline decision (§5.3) ----------------------------------------------------------

export interface OfflineDecisionInput {
  raw: string;
  performance_ids: string[];
  gate_id: string | null;
  gate_name: string | null;
  direction: 'ENTRY' | 'EXIT';
  input_method: InputMethod;
  now: string;
  clock_offset_ms: number | null;
}

const REASONS: Record<string, string> = {
  INVALID: 'Not a valid ticket',
  REFUNDED: 'This ticket was refunded',
  REVOKED: 'This ticket was revoked',
  TRANSFERRED: 'Superseded: the ticket was transferred or reissued; ask the guest to refresh their ticket',
  // A getter, so the words are the deployment's even when they arrive after this module loads (D-378).
  get WRONG_PERFORMANCE() {
    return `Ticket is for a different ${dateWords().one}`;
  },
  WRONG_ACCESS_ZONE: 'Ticket does not admit through this gate',
  NOT_YET_VALID: 'Admission has not opened yet',
  EXPIRED: 'Admission has closed',
};

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Decides from the bundle, records the local admission and queues the scan for sync. Returns null when no bundle covers the performances. */
/**
 * Whether this device may admit anyone with the network down (D-329).
 *
 * The answer is the one the server gave at the last sync, because an offline
 * scanner cannot ask: that is why the switch warns it is not safe to flip
 * mid-door. Any bundle for the chosen performances saying no is a no.
 */
export async function offlineAdmissionAllowed(performanceIds: string[]): Promise<boolean> {
  const bundles = (await Promise.all(performanceIds.map((id) => db.bundles.get(id)))).filter((b): b is StoredBundle => Boolean(b));
  return bundles.every((bundle) => bundle.offline_admission !== false);
}

export async function decideOffline(input: OfflineDecisionInput): Promise<ScanOutcome | null> {
  const bundles = (await Promise.all(input.performance_ids.map((id) => db.bundles.get(id)))).filter((b): b is StoredBundle => Boolean(b));
  if (bundles.length === 0) return null;
  const scanId = crypto.randomUUID();
  const raw = input.raw.trim();
  const rawSha = await sha256Hex(raw);
  let ticket: StoredTicket | undefined;
  let presented: number | null = null;
  let rejection: ScanResult | null = null;
  let reasonText: string | null = null;

  if (raw.includes('.')) {
    const parsed = parseCredential(raw);
    if (!parsed.ok) {
      // A season pass (D-373)?
      const pass = await decidePassFromBundles(raw, input, bundles, scanId, rawSha);
      if (pass) return pass;
      rejection = 'INVALID';
      reasonText = 'The code could not be read';
    } else {
      const key = await db.keys.get(parsed.payload.pid);
      if (!key || !(await verifyCredential(key.key, parsed))) {
        rejection = 'INVALID';
        reasonText = key ? 'The code is not genuine' : `Ticket is for ${dateWords().a} this device has no bundle for`;
      } else {
        ticket = await db.tickets.get(parsed.payload.tid);
        presented = parsed.payload.cv;
        if (!ticket || ticket.performance_id !== parsed.payload.pid) {
          rejection = 'INVALID';
          reasonText = 'No such ticket in the bundle';
          ticket = undefined;
        }
      }
    }
  } else {
    ticket = await db.tickets.where('short_code').equals(raw.toUpperCase()).first();
    if (!ticket) {
      rejection = 'INVALID';
      reasonText = 'No ticket has that code';
    } else presented = ticket.cv;
  }

  const performance = ticket ? bundles.find((b) => b.performance_id === ticket!.performance_id) : undefined;
  if (!rejection && ticket) {
    const lifecycle = lifecycleScanRejection(ticket.status as TicketStatus, ticket.cv, presented ?? ticket.cv);
    if (lifecycle) rejection = lifecycle;
    else if (!performance) rejection = 'WRONG_PERFORMANCE';
    else if (performance.performance.admission_opens_at && input.now < performance.performance.admission_opens_at) rejection = 'NOT_YET_VALID';
    else if (performance.performance.admission_closes_at && input.now > performance.performance.admission_closes_at) rejection = 'EXPIRED';
    else if (input.gate_id && ticket.zones !== null) {
      const admits = performance.gate_zones[input.gate_id] ?? [];
      if (admits.length > 0 && !ticket.zones.some((z) => admits.includes(z))) rejection = 'WRONG_ACCESS_ZONE';
    }
  }

  const target = performance ?? bundles[0]!;
  let result: ScanResult;
  let previous: ScanOutcome['previous'] = null;
  let reasonCode: string | null = rejection;
  if (rejection) {
    result = rejection;
    reasonText ??= REASONS[rejection] ?? null;
  } else {
    const local = await db.admissions.get(ticket!.tid);
    if (local) {
      result = 'ALREADY_SCANNED';
      reasonCode = 'DUPLICATE';
      reasonText = 'Already admitted on this device';
      previous = { scan_id: local.scan_id, at: local.at, gate: local.gate_name, device: 'this device', staff: null };
    } else if (ticket!.admitted) {
      result = 'ALREADY_SCANNED';
      reasonCode = 'DUPLICATE';
      reasonText = `Already admitted at ${ticket!.admitted.gate ?? 'a gate'} (as of the last sync)`;
      previous = { scan_id: '', at: ticket!.admitted.at, gate: ticket!.admitted.gate, device: ticket!.admitted.device, staff: null };
    } else {
      result = 'OFFLINE_ACCEPTED';
      await db.admissions.put({ tid: ticket!.tid, performance_id: ticket!.performance_id, scan_id: scanId, at: input.now, gate_id: input.gate_id, gate_name: input.gate_name });
    }
  }

  // Only decisions the server can adjudicate are queued: acceptances and definite rejections (a local duplicate is a display, the server already knows).
  if (result !== 'ALREADY_SCANNED' || !previous || previous.scan_id === '') {
    await db.scan_queue.put({
      scan_id: scanId,
      ticket_id: ticket?.tid ?? null,
      credential_version_presented: presented,
      performance_id: target.performance_id,
      gate_id: input.gate_id,
      direction: input.direction,
      input_method: input.input_method,
      device_result: result,
      reason_code: reasonCode,
      raw_sha256: rawSha,
      device_timestamp: input.now,
      device_clock_offset_ms: input.clock_offset_ms,
      bundle_id: target.bundle_id,
      queued_at: input.now,
      seat_label: ticket?.seat ?? null,
    });
  }
  const admittedHere = await db.admissions.where('performance_id').equals(target.performance_id).count();
  return {
    scan_id: scanId,
    result,
    color: result === 'OFFLINE_ACCEPTED' ? 'GREEN' : result === 'NOT_YET_VALID' ? 'YELLOW' : 'RED',
    counts_as_admission: result === 'OFFLINE_ACCEPTED',
    reason_code: reasonCode,
    reason_text: reasonText,
    ticket: ticket ? { id: ticket.tid, short_code: ticket.short_code, ticket_type: ticket.type, seat_label: ticket.seat, holder_name: ticket.holder, zone_id: null, credential_version: ticket.cv } : null,
    previous,
    counters: { gate_admitted: admittedHere, performance_admitted: (await db.tickets.where('performance_id').equals(target.performance_id).filter((t) => t.admitted !== null).count()) + admittedHere, performance_live_tickets: await db.tickets.where('performance_id').equals(target.performance_id).filter((t) => t.status === 'ISSUED' || t.status === 'REISSUED' || t.status === 'TRANSFERRED').count() },
    offline: true,
  };
}

/**
 * A season pass offline (D-373): decided against the first of tonight's bundles
 * whose season the pass covers, queued for sync with its pass id, and recorded
 * locally so the same pass is refused a second time on this device.
 */
async function decidePassFromBundles(raw: string, input: OfflineDecisionInput, bundles: StoredBundle[], scanId: string, rawSha: string): Promise<ScanOutcome | null> {
  const nights = (await Promise.all(bundles.map((b) => db.pass_keys.get(b.performance_id)))).filter((k): k is StoredPassKey => Boolean(k));
  let decided: Awaited<ReturnType<typeof decidePassOffline>> = null;
  let target = bundles[0]!;
  for (const night of nights.length > 0 ? nights : [null]) {
    const performanceId = night?.performance_id ?? target.performance_id;
    decided = await decidePassOffline(raw, night ? { season_id: night.season_id, game_date: night.game_date, key: night.key } : null, {
      pass: (id) => db.passes.get(passKey(performanceId, id)),
      ticketFor: async (passId) => {
        const ticket = await db.tickets.where('pass').equals(passId).filter((t) => t.performance_id === performanceId && ['ISSUED', 'REISSUED', 'TRANSFERRED'].includes(t.status)).first();
        if (!ticket) return undefined;
        return { tid: ticket.tid, admitted: Boolean(ticket.admitted) || Boolean(await db.admissions.get(ticket.tid)) };
      },
      admittedLocally: async (passId) => Boolean(await db.pass_admissions.get(passKey(performanceId, passId))),
    });
    if (!decided) return null;
    target = bundles.find((b) => b.performance_id === performanceId) ?? target;
    if (decided.result !== 'WRONG_EVENT') break;
  }
  if (!decided) return null;
  const performance = target.performance;
  if (decided.result === 'OFFLINE_ACCEPTED') {
    if (performance.admission_opens_at && input.now < performance.admission_opens_at) decided = { ...decided, result: 'NOT_YET_VALID', reason_code: 'NOT_YET_VALID', reason_text: REASONS['NOT_YET_VALID']! };
    else if (performance.admission_closes_at && input.now > performance.admission_closes_at) decided = { ...decided, result: 'EXPIRED', reason_code: 'EXPIRED', reason_text: REASONS['EXPIRED']! };
  }
  const accepted = decided.result === 'OFFLINE_ACCEPTED';
  if (accepted) {
    if (decided.ticket_id) await db.admissions.put({ tid: decided.ticket_id, performance_id: target.performance_id, scan_id: scanId, at: input.now, gate_id: input.gate_id, gate_name: input.gate_name });
    else if (decided.pass) await db.pass_admissions.put({ key: passKey(target.performance_id, decided.pass.id), performance_id: target.performance_id, pass_id: decided.pass.id, scan_id: scanId, at: input.now });
  }
  // A local duplicate is a display; everything else is adjudicated by the server.
  if (decided.result !== 'ALREADY_SCANNED') {
    await db.scan_queue.put({
      scan_id: scanId,
      ticket_id: decided.ticket_id,
      credential_version_presented: decided.presented_cv,
      performance_id: target.performance_id,
      gate_id: input.gate_id,
      direction: input.direction,
      input_method: input.input_method,
      device_result: decided.result,
      reason_code: decided.reason_code,
      raw_sha256: rawSha,
      device_timestamp: input.now,
      device_clock_offset_ms: input.clock_offset_ms,
      bundle_id: target.bundle_id,
      season_pass_id: decided.pass?.id ?? null,
      queued_at: input.now,
      seat_label: decided.pass?.label ?? null,
    });
  }
  const admittedHere = (await db.admissions.where('performance_id').equals(target.performance_id).count()) + (await db.pass_admissions.where('performance_id').equals(target.performance_id).count());
  return {
    scan_id: scanId,
    result: decided.result,
    color: accepted ? 'GREEN' : decided.result === 'NOT_YET_VALID' ? 'YELLOW' : 'RED',
    counts_as_admission: accepted,
    reason_code: decided.reason_code,
    reason_text: decided.reason_text,
    ticket: null,
    previous: null,
    counters: { gate_admitted: admittedHere, performance_admitted: admittedHere, performance_live_tickets: await db.tickets.where('performance_id').equals(target.performance_id).count() },
    season_pass: decided.pass ? { id: decided.pass.id, label: decided.pass.label, number: '', holder_name: null } : null,
    offline: true,
  };
}

export async function pendingScans(): Promise<QueuedScan[]> {
  return db.scan_queue.orderBy('scan_id').toArray();
}

export async function forgetSynced(scanIds: string[]): Promise<void> {
  await db.scan_queue.bulkDelete(scanIds);
}

/** After a successful sync the local admission map is no longer the truth; the next delta brings the server's. */
export async function clearLocalAdmissions(performanceId: string): Promise<void> {
  await db.admissions.where('performance_id').equals(performanceId).delete();
  await db.pass_admissions.where('performance_id').equals(performanceId).delete();
}

export async function wipeEverything(): Promise<void> {
  await db.transaction('rw', [db.bundles, db.tickets, db.keys, db.admissions, db.scan_queue, db.passes, db.pass_keys, db.pass_admissions], async () => {
    await Promise.all([db.bundles.clear(), db.tickets.clear(), db.keys.clear(), db.admissions.clear(), db.scan_queue.clear(), db.passes.clear(), db.pass_keys.clear(), db.pass_admissions.clear()]);
  });
}
